/** @jsxImportSource @emotion/react */
import { useAtom } from 'jotai';
import useNotifications from '../../../../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../../../../login/v1/utility';
import useAddonPurchase from '../../../../../shared/hooks/use-addon-purchase';
import { useCallback } from 'react';
import { openPopup } from '../../../../../shared/utils/popup';
import QSModal from '../../../../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../../../../shared/components/atoms';
import ProgressiveImage from '../../../../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../../../../renew-plan/v1/typings';
import { Close } from '@mui/icons-material';
import Loader from '../../../../../shared/components/molecules/loader';
import WaVoiceImage from './assets/ip-restrictions-image.png';
import { upgradeIpRestrictionsAtom } from '../../../../home/v1/states/upgrade-plan';
import { ReactComponent as CloudCheckIcon } from './assets/cloud-check.svg';
import { ReactComponent as DisallowIcon } from './assets/disallow.svg';
import { ReactComponent as KingCircleIcon } from './assets/king-circle.svg';

const FEATURES = [
  {
    featureLabel: 'Allowed IP address list',
    desc: 'Lock down your organisation’s access to specific IP addresses like your office or VPN.',
    icon: CloudCheckIcon,
    iconColor: '#3D906D',
  },
  {
    featureLabel: 'Block Unauthorised Access',
    desc: 'Automatically reject login attempts from unknown locations or malicious actors.',
    icon: DisallowIcon,
    iconColor: '#EF5350',
  },
  {
    featureLabel: 'Complete Peace of Mind',
    desc: 'Only organisation owner can edit this setting to ensure malicious actions within the team',
    icon: KingCircleIcon,
    iconColor: '#596BD7',
  },
];

export default function IpRestrictionsUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(upgradeIpRestrictionsAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();

  let contactUsLink = `https://wa.me/918356849474?text=Hi,%20I’m%20interested%20in%20purchasing%20the%20IP%20Access%20Control%20feature%20to%20secure%20my%20customer%20data.%20Could%20you%20share%20the%20pricing%20and%20setup%20process?`;
  if (hideDTDetails) {
    contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=Hi,%20I’m%20interested%20in%20purchasing%20the%20IP%20Access%20Control%20feature%20to%20secure%20my%20customer%20data.%20Could%20you%20share%20the%20pricing%20and%20setup%20process?`;
  }
  const onClose = useCallback(() => {
    setUpgradeModal({
      isModalActive: false,
    });
  }, [setUpgradeModal]);

  const onClickContactUs = useCallback(async () => {
    try {
      openPopup(contactUsLink, '_blank');
      // if (hideDTDetails) {
      //   openPopup(contactUsLink, '_blank');
      // } else {
      //   await purchaseAddon(PlanAddonTypes.IP_RESTRICTIONS);
      // }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  if (!upgradeModal) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal.isModalActive}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
              width: 320px;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '374px' }} src={WaVoiceImage} />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 0;
              padding-left: 24px;
              max-width: 356px;
            `}
          >
            <span
              css={css`
                border-radius: 4px;
                padding: 6px 8px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                font-weight: 600;
                color: #596bd7;
                background-color: #596bd714;
              `}
            >
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                IP Access Control
              </span>
            </span>
            <QSTypography
              css={css`
                font-weight: 600;
                padding-right: 12px;
                font-size: 20px;
                overflow: hidden;
                line-height: 28px;
                margin-bottom: 16px;
                margin-top: 8px;
              `}
            >
              Fortify your customer’s data by only allowing access to IP address of your choice
            </QSTypography>

            {FEATURES.map((feature, key) => {
              const Icon = feature?.icon;
              return (
                <div
                  key={key}
                  css={css`
                    display: inline-flex;
                    align-items: flex-start;
                    justify-content: flex-start;
                    margin-bottom: 10px;
                    font-weight: 500;
                    gap: 12px;
                  `}
                >
                  <div
                    style={{
                      height: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: feature.iconColor ?? '#2d2d2d',
                    }}
                  >
                    <Icon width={24} height={24} />
                  </div>
                  <span
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 4px;
                    `}
                  >
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 16.94px;
                        text-align: left;
                        color: #2d2d2d;
                      `}
                    >
                      {feature?.featureLabel}
                    </div>
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 12px;
                        font-weight: 500;
                        line-height: 14.52px;
                        color: #7a7a7a;
                      `}
                    >
                      {feature?.desc}
                    </div>
                  </span>
                </div>
              );
            })}
            <div
              css={css`
                margin-top: 14px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 10px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                `}
                onClick={onClickContactUs}
              >
                {hideDTDetails ? (
                  'Contact Us'
                ) : (
                  <>
                    {purchaseLoading ? (
                      <Loader
                        size={16}
                        loaderProps={{
                          sx: {
                            color: 'white',
                          },
                        }}
                      />
                    ) : (
                      'Contact Us'
                    )}
                  </>
                )}
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
}
